import { defineStore } from 'pinia'
import { InteractionRequiredAuthError } from '@azure/msal-browser'

import { useClientStore } from '@/stores/client'
import { useProjectStore } from '@/stores/project'
import { getProjectByClientId } from '@/services/implementationmanagement.api.js'
import router from '@/router'

import { msalInstance } from '@/plugins/msal'

const LOGIN_SCOPES = ['openid', 'profile', 'email']
const POLICY_NAME = 'OneTimeCodeLogin'

function isInteractionRequiredError(error) {
  if (!error) return false

  if (error instanceof InteractionRequiredAuthError) return true

  const errorCode = error?.errorCode || error?.code || ''
  const message = String(error?.message || '').toLowerCase()

  return (
    ['interaction_required', 'login_required', 'consent_required'].includes(errorCode) ||
    message.includes('interaction_required') ||
    message.includes('aadsts160021') ||
    message.includes('user session which does not exist')
  )
}

function isIframeReloadError(error) {
  if (!error) return false

  const errorCode = error?.errorCode || error?.code || ''
  const message = String(error?.message || '').toLowerCase()

  return errorCode === 'block_iframe_reload' || message.includes('block_iframe_reload')
}

export const useIdentityStore = defineStore('identity', {
  state: () => ({
    isAuthenticated: false,
    account: null,
    idTokenClaims: null,
    clients: null,
    type: '',
    intermediary: null,

    selectedClientId: null,
    isSwitchingClient: false,
    switchClientError: null,

    isRefreshingClaims: false,
    refreshClaimsError: null,
  }),

  persist: true,

  actions: {
    setUser(account, claims) {
      this.isAuthenticated = true
      this.account = account
      this.idTokenClaims = claims || null
      this.clients = claims?.associatedClients || []
      this.type = claims?.userType || ''
      this.refreshClaimsError = null
      this.switchClientError = null
    },

    clearUser() {
      this.isAuthenticated = false
      this.account = null
      this.idTokenClaims = null
      this.clients = null
      this.type = ''
      this.intermediary = null

      this.selectedClientId = null
      this.isSwitchingClient = false
      this.switchClientError = null

      this.isRefreshingClaims = false
      this.refreshClaimsError = null

      const clientStore = useClientStore()
      const projectStore = useProjectStore()

      if (clientStore.clearClient) clientStore.clearClient()
      if (projectStore.clearProject) projectStore.clearProject()
    },

    setIntermediary(intermediary) {
      this.intermediary = intermediary
    },

    getUser() {
      return this.account
    },

    getClients() {
      return this.clients
    },

    getIntermediary() {
      return this.intermediary
    },

    getActiveAccount(msalInstance) {
      if (!msalInstance) return null

      const active =
        msalInstance.getActiveAccount() ||
        this.account ||
        msalInstance.getAllAccounts()?.[0] ||
        null

      if (active && !msalInstance.getActiveAccount()) {
        msalInstance.setActiveAccount(active)
      }

      return active
    },

    async syncFromMsal(msalInstance) {
      if (!msalInstance) throw new Error('MSAL instance not provided')

      const account = this.getActiveAccount(msalInstance)

      if (!account) {
        this.clearUser()
        return null
      }

      this.isAuthenticated = true
      this.account = account

      if (account.idTokenClaims) {
        this.setUser(account, account.idTokenClaims)
      }

      return account
    },

    async login(msalInstance, extra = {}) {
      if (!msalInstance) throw new Error('MSAL instance not provided')

      await msalInstance.loginRedirect({
        scopes: LOGIN_SCOPES,
        extraQueryParameters: { p: POLICY_NAME },
        ...extra,
      })
    },

    async logout() {
      this.clearUser()

      sessionStorage.setItem('logout-in-progress', 'true')
      if (!msalInstance) return

      await msalInstance.logoutRedirect({
        account: undefined,
      })
      console.log("Logout initiated, redirecting to login page.")
      router.push('/login')
    },

    async ensureAuthenticated(msalInstance) {
      if (!msalInstance) throw new Error('MSAL instance not provided')

      const account = this.getActiveAccount(msalInstance)

      if (!account) {
        this.clearUser()
        await this.login(msalInstance)
        return null
      }

      if (!this.isAuthenticated) {
        await this.syncFromMsal(msalInstance)
      }

      return account
    },

    async refreshClaims(msalInstance) {
      if (!msalInstance) throw new Error('MSAL instance not provided')

      if (this.isRefreshingClaims) {
        return this.idTokenClaims
      }

      this.isRefreshingClaims = true
      this.refreshClaimsError = null

      try {
        const account = await this.ensureAuthenticated(msalInstance)

        if (!account) return null

        const tokenResult = await msalInstance.acquireTokenSilent({
          account,
          scopes: LOGIN_SCOPES,
          forceRefresh: true,
          extraQueryParameters: { p: POLICY_NAME },
        })

        this.setUser(account, tokenResult.idTokenClaims)
        return tokenResult.idTokenClaims
      } catch (error) {
        console.error('refreshClaims failed', error)
        this.refreshClaimsError = error?.message || 'Failed to refresh claims'

        if (isIframeReloadError(error) || isInteractionRequiredError(error)) {
          this.clearUser()
          await this.login(msalInstance)
          return null
        }

        throw error
      } finally {
        this.isRefreshingClaims = false
      }
    },

    async getAccessToken(msalInstance, scopes = LOGIN_SCOPES) {
      if (!msalInstance) throw new Error('MSAL instance not provided')

      const account = await this.ensureAuthenticated(msalInstance)

      if (!account) {
        throw new Error('No active MSAL account. User is not logged in.')
      }

      try {
        const tokenResult = await msalInstance.acquireTokenSilent({
          account,
          scopes,
          extraQueryParameters: { p: POLICY_NAME },
        })

        if (tokenResult?.idTokenClaims) {
          this.setUser(account, tokenResult.idTokenClaims)
        }

        return tokenResult.accessToken
      } catch (error) {
        console.error('getAccessToken failed', error)

        if (isIframeReloadError(error) || isInteractionRequiredError(error)) {
          this.clearUser()
          await this.login(msalInstance, { scopes })
          return null
        }

        throw error
      }
    },

    async selectClient(clientId) {
      this.isSwitchingClient = true
      this.switchClientError = null

      try {
        let clientsArray = []

        if (Array.isArray(this.clients)) {
          clientsArray = this.clients
        } else if (Array.isArray(this.idTokenClaims?.associatedClients)) {
          clientsArray = this.idTokenClaims.associatedClients
        } else if (typeof this.idTokenClaims?.associatedClients === 'string') {
          // Handle single value or comma-separated values
          clientsArray = this.idTokenClaims.associatedClients.includes(',')
            ? this.idTokenClaims.associatedClients.split(',').map(x => x.trim()).filter(Boolean)
            : [this.idTokenClaims.associatedClients]
        } else if (this.idTokenClaims?.associatedClients && typeof this.idTokenClaims.associatedClients === 'object') {
          // Handle single object
          clientsArray = [this.idTokenClaims.associatedClients]
        }

        const allowed = clientsArray.some(c =>
          typeof c === 'string'
            ? c === clientId
            : (c?.id === clientId || c?.clientId === clientId)
        )

        if (clientsArray.length && !allowed && this.type !== 'Administrator') {
          throw new Error('Client not allowed for this user')
        }

        const projectResponse = await getProjectByClientId(clientId)

        const project = projectResponse?.data
        const client = project?.client

        if (projectResponse?.status !== 200 || !client || !project) {
          throw new Error('Error fetching project/client')
        }

        const clientStore = useClientStore()
        const projectStore = useProjectStore()

        clientStore.setClient(client)
        projectStore.setProject(project)

        this.selectedClientId = clientId
        return project
      } catch (err) {
        console.error(err)
        this.switchClientError = err?.message ?? 'Failed to switch client'
        throw err
      } finally {
        this.isSwitchingClient = false
      }
    },
  },
})